// src/types.ts
var DaemonApiError = class extends Error {
  constructor(path, status, familiar) {
    super(`DAEMON ${path} failed: ${status}`);
    this.path = path;
    this.status = status;
    this.familiar = familiar;
    this.name = "DaemonApiError";
  }
  path;
  status;
  familiar;
};

// src/sse.ts
async function parseChatSSE(stream, handlers, signal) {
  const reader = stream.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  try {
    while (true) {
      if (signal?.aborted) return;
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let nl;
      while ((nl = buffer.indexOf("\n\n")) !== -1) {
        const frame = buffer.slice(0, nl);
        buffer = buffer.slice(nl + 2);
        for (const line of frame.split("\n")) {
          if (!line.startsWith("data: ")) continue;
          const data = line.slice(6);
          if (data === "[DONE]") {
            handlers.onDone();
            return;
          }
          dispatch(data, handlers);
        }
      }
    }
    handlers.onDone();
  } catch (err) {
    handlers.onError(err instanceof Error ? err : new Error(String(err)));
  }
}
function dispatch(data, h) {
  let obj;
  try {
    obj = JSON.parse(data);
  } catch {
    return;
  }
  if (typeof obj.session_id === "string") h.onSessionId?.(obj.session_id);
  else if (typeof obj.text === "string") h.onChunk(obj.text);
  else if (obj.tool_call) h.onToolCall(obj.tool_call);
  else if (obj.tool_result) h.onToolResult(obj.tool_result);
  else if (obj.confirmation_required) h.onConfirm(obj.confirmation_required);
  else if (obj.confirmation_error) h.onConfirmationError?.(obj.confirmation_error);
}

// src/webTransport.ts
function createWebTransport({ baseUrl, onUnauthorized }) {
  let currentHandlers = null;
  async function request(path, init) {
    const res = await fetch(`${baseUrl}${path}`, { credentials: "include", ...init });
    if (res.status === 401) onUnauthorized();
    const familiar = res.headers.get("X-Diwata-Familiar");
    let body_json = null;
    try {
      body_json = await res.json();
    } catch {
    }
    return { status: res.status, body_json, familiar };
  }
  return {
    get(path) {
      return request(path);
    },
    post(path, body) {
      return request(path, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
      });
    },
    chat(message, sessionId, handlers) {
      currentHandlers = handlers;
      const ctrl = new AbortController();
      void (async () => {
        try {
          const res = await fetch(`${baseUrl}/chat`, {
            method: "POST",
            credentials: "include",
            signal: ctrl.signal,
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ message, session_id: sessionId })
          });
          if (res.status === 401) {
            onUnauthorized();
            handlers.onError(new DaemonApiError("/chat", 401, null));
            return;
          }
          if (!res.body) {
            handlers.onError(new Error("no response body"));
            return;
          }
          handlers.onFamiliar?.(res.headers.get("X-Diwata-Familiar"));
          await parseChatSSE(res.body, handlers, ctrl.signal);
        } catch (err) {
          if (!ctrl.signal.aborted) {
            handlers.onError(err instanceof Error ? err : new Error(String(err)));
          }
        }
      })();
      return () => ctrl.abort();
    },
    async confirm(token, decision) {
      const handlers = currentHandlers;
      const res = await fetch(`${baseUrl}/chat`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ confirm_token: token, confirm_decision: decision })
      });
      if (res.status === 401) {
        onUnauthorized();
        return;
      }
      if (res.body && handlers) await parseChatSSE(res.body, handlers);
    },
    async transcribe(bytes) {
      const form = new FormData();
      form.append("audio", new Blob([bytes], { type: "audio/webm" }), "audio.webm");
      const res = await fetch(`${baseUrl}/transcribe`, {
        method: "POST",
        credentials: "include",
        body: form
      });
      if (!res.ok) {
        throw new DaemonApiError("/transcribe", res.status, res.headers.get("X-Diwata-Familiar"));
      }
      return await res.json();
    }
  };
}

// src/context.tsx
import { createContext, useContext } from "react";
import { jsx } from "react/jsx-runtime";
var TransportContext = createContext(null);
function FamiliarProvider({ transport, children }) {
  return /* @__PURE__ */ jsx(TransportContext.Provider, { value: transport, children });
}
function useDaemon() {
  const t = useContext(TransportContext);
  if (!t) throw new Error("useDaemon must be used within <FamiliarProvider>");
  return t;
}

// src/useBrief.ts
import { useQuery } from "@tanstack/react-query";
function useBrief(options) {
  const transport = useDaemon();
  return useQuery({
    queryKey: ["hearth", "brief"],
    enabled: options?.enabled ?? true,
    refetchInterval: options?.refetchInterval,
    queryFn: async () => {
      const res = await transport.get("/brief");
      if (res.status < 200 || res.status >= 300) {
        throw new DaemonApiError("/brief", res.status, res.familiar);
      }
      return { data: res.body_json, familiar: res.familiar };
    }
  });
}

// src/BriefPanel.tsx
import { Badge, Card, Spinner, Stack, Text } from "@diwata/ironvale";
import { jsx as jsx2, jsxs } from "react/jsx-runtime";
var mutedStyle = { color: "var(--ae-color-text-muted)" };
function BriefPanel({ renderRepoLink }) {
  const { data, isLoading, error } = useBrief();
  if (isLoading) return /* @__PURE__ */ jsx2(Spinner, {});
  if (error || !data) {
    return /* @__PURE__ */ jsx2(Card, { children: /* @__PURE__ */ jsx2(Text, { style: { color: "var(--ae-color-feedback-error)" }, children: "Could not reach DAEMON." }) });
  }
  const brief = data.data;
  return /* @__PURE__ */ jsxs(Stack, { gap: "md", children: [
    brief.repos.map((repo) => {
      const card = /* @__PURE__ */ jsx2(Card, { children: /* @__PURE__ */ jsxs(Stack, { gap: "sm", children: [
        /* @__PURE__ */ jsxs("div", { style: { display: "flex", justifyContent: "space-between", alignItems: "center" }, children: [
          /* @__PURE__ */ jsx2(Text, { style: { fontWeight: 600 }, children: repo.repo }),
          /* @__PURE__ */ jsx2(Badge, { variant: "neutral", children: repo.branch })
        ] }),
        repo.current_focus && /* @__PURE__ */ jsx2(Text, { variant: "caption", style: mutedStyle, children: repo.current_focus })
      ] }) }, repo.repo);
      return renderRepoLink ? renderRepoLink(repo.repo, card) : card;
    }),
    brief.top_memories.length > 0 && /* @__PURE__ */ jsxs(Stack, { gap: "sm", children: [
      /* @__PURE__ */ jsx2(Text, { variant: "caption", style: mutedStyle, children: "DAEMON Memory" }),
      brief.top_memories.map((m, i) => /* @__PURE__ */ jsx2(Card, { children: /* @__PURE__ */ jsx2(Text, { children: m }) }, i))
    ] })
  ] });
}

// src/ChatPanel.tsx
import { useEffect, useRef, useState } from "react";
import { Badge as Badge2, Button, Card as Card2, ChatSurface } from "@diwata/ironvale";

// src/recorder.ts
var Recorder = class {
  rec;
  chunks = [];
  /**
   * Open the mic (prompting for permission on first use) and start recording.
   * Rejects if the operator denies mic access or no device is available.
   */
  async start() {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    this.chunks = [];
    this.rec = new MediaRecorder(stream, { mimeType: "audio/webm" });
    this.rec.ondataavailable = (event) => {
      if (event.data.size > 0) this.chunks.push(event.data);
    };
    this.rec.start();
  }
  /**
   * End the clip and resolve its recorded bytes, then release the mic track.
   * Rejects if called before `start()`.
   */
  stop() {
    return new Promise((resolve, reject) => {
      const rec = this.rec;
      if (!rec) {
        reject(new Error("recorder was not started"));
        return;
      }
      rec.onstop = async () => {
        const blob = new Blob(this.chunks, { type: "audio/webm" });
        const bytes = new Uint8Array(await blob.arrayBuffer());
        rec.stream.getTracks().forEach((track) => track.stop());
        this.rec = void 0;
        resolve(bytes);
      };
      rec.stop();
    });
  }
};

// src/ChatPanel.tsx
import { jsx as jsx3, jsxs as jsxs2 } from "react/jsx-runtime";
function settleDraft(thread, draftId) {
  return thread.flatMap((entry) => {
    if (entry.kind !== "message" || entry.id !== draftId) return [entry];
    if (entry.text === "") return [];
    return [{ ...entry, pending: false }];
  });
}
function ChatPanel({ sessionId, familiarName = "your familiar", onFamiliar, mic = true }) {
  const transport = useDaemon();
  const [thread, setThread] = useState([]);
  const [streaming, setStreaming] = useState(false);
  const [pendingConfirm, setPendingConfirm] = useState(null);
  const [draft, setDraft] = useState("");
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const recorderRef = useRef(null);
  if (recorderRef.current === null) recorderRef.current = new Recorder();
  const streamingRef = useRef(false);
  const draftIdRef = useRef(null);
  const pendingConfirmRef = useRef(null);
  const runningToolIdsRef = useRef(/* @__PURE__ */ new Set());
  const idRef = useRef(0);
  const familiarNameRef = useRef(familiarName);
  familiarNameRef.current = familiarName;
  const onFamiliarRef = useRef(onFamiliar);
  onFamiliarRef.current = onFamiliar;
  const cancelRef = useRef(null);
  const nextId = (prefix) => `${prefix}-${idRef.current++}`;
  const appendChunk = (text) => {
    if (!streamingRef.current) return;
    if (draftIdRef.current === null) {
      const draftId2 = nextId("assistant");
      draftIdRef.current = draftId2;
      setThread((prev) => [
        ...prev,
        { kind: "message", id: draftId2, role: "assistant", text: "", pending: false }
      ]);
    }
    const draftId = draftIdRef.current;
    setThread(
      (prev) => prev.map(
        (entry) => entry.kind === "message" && entry.id === draftId ? { ...entry, text: entry.text + text, pending: false } : entry
      )
    );
  };
  const onToolCall = (call) => {
    if (!streamingRef.current) return;
    runningToolIdsRef.current.add(call.id);
    const draftId = draftIdRef.current;
    draftIdRef.current = null;
    setThread((prev) => [
      ...settleDraft(prev, draftId),
      {
        kind: "tool",
        id: nextId("tool"),
        callId: call.id,
        name: call.name,
        args: call.arguments ?? {},
        status: "running"
      }
    ]);
  };
  const onToolResult = (result) => {
    const status = result.is_error ? "error" : "ok";
    if (runningToolIdsRef.current.delete(result.id)) {
      setThread(
        (prev) => prev.map(
          (entry) => entry.kind === "tool" && entry.callId === result.id && entry.status === "running" ? { ...entry, status, result: result.content } : entry
        )
      );
      return;
    }
    const draftId = draftIdRef.current;
    draftIdRef.current = null;
    setThread((prev) => [
      ...settleDraft(prev, draftId),
      {
        kind: "tool",
        id: nextId("tool"),
        callId: result.id,
        name: result.name,
        args: {},
        status,
        result: result.content
      }
    ]);
  };
  const onConfirmRequired = (confirmation) => {
    const draftId = draftIdRef.current;
    draftIdRef.current = null;
    pendingConfirmRef.current = confirmation.token;
    setPendingConfirm(confirmation.token);
    setThread((prev) => [
      ...settleDraft(prev, draftId),
      {
        kind: "confirm",
        id: nextId("confirm"),
        token: confirmation.token,
        name: confirmation.name,
        args: confirmation.arguments ?? {},
        level: confirmation.permission_level,
        status: "pending"
      }
    ]);
  };
  const onConfirmationError = (err) => {
    if (pendingConfirmRef.current === err.token) {
      pendingConfirmRef.current = null;
      setPendingConfirm(null);
    }
    setThread((prev) => [
      ...prev.map(
        (e) => e.kind === "confirm" && e.token === err.token && e.status === "pending" ? { ...e, status: "denied" } : e
      ),
      { kind: "message", id: nextId("confirm-error"), role: "system", text: `Confirmation failed: ${err.reason}` }
    ]);
  };
  const finishStream = () => {
    if (!streamingRef.current) return;
    streamingRef.current = false;
    const draftId = draftIdRef.current;
    draftIdRef.current = null;
    setStreaming(false);
    setThread((prev) => settleDraft(prev, draftId));
  };
  const failStream = (message) => {
    if (!streamingRef.current) return;
    streamingRef.current = false;
    const draftId = draftIdRef.current;
    draftIdRef.current = null;
    pendingConfirmRef.current = null;
    setStreaming(false);
    setPendingConfirm(null);
    setThread((prev) => [
      ...settleDraft(prev, draftId),
      {
        kind: "message",
        id: `error-${draftId ?? nextId("e")}`,
        role: "system",
        text: `${familiarNameRef.current} was interrupted: ${message}`
      }
    ]);
  };
  const buildHandlers = () => ({
    onChunk: appendChunk,
    onToolCall,
    onToolResult,
    onConfirm: onConfirmRequired,
    onConfirmationError,
    onFamiliar: (familiar) => onFamiliarRef.current?.(familiar),
    onDone: finishStream,
    onError: (err) => failStream(err.message)
  });
  useEffect(() => {
    return () => cancelRef.current?.();
  }, []);
  const send = (text) => {
    if (streamingRef.current || pendingConfirmRef.current !== null) return;
    const draftId = nextId("assistant");
    streamingRef.current = true;
    draftIdRef.current = draftId;
    setStreaming(true);
    setThread((prev) => [
      ...prev,
      { kind: "message", id: nextId("user"), role: "user", text },
      { kind: "message", id: draftId, role: "assistant", text: "", pending: true }
    ]);
    cancelRef.current?.();
    cancelRef.current = transport.chat(text, sessionId, buildHandlers());
  };
  const respond = (entry, decision) => {
    if (pendingConfirmRef.current !== entry.token) return;
    const decided = decision === "confirm" ? "confirmed" : "denied";
    pendingConfirmRef.current = null;
    setPendingConfirm(null);
    const draftId = nextId("assistant");
    streamingRef.current = true;
    draftIdRef.current = draftId;
    setStreaming(true);
    setThread((prev) => [
      ...prev.map(
        (e) => e.kind === "confirm" && e.id === entry.id ? { ...e, status: decided } : e
      ),
      { kind: "message", id: draftId, role: "assistant", text: "", pending: true }
    ]);
    void transport.confirm(entry.token, decision).catch((error) => {
      failStream(error instanceof Error ? error.message : String(error));
    });
  };
  const noteVoiceError = (message) => {
    setThread((prev) => [
      ...prev,
      { kind: "message", id: nextId("voice-error"), role: "system", text: message }
    ]);
  };
  const startRecording = async () => {
    if (recording || transcribing || streamingRef.current || pendingConfirmRef.current !== null) {
      return;
    }
    try {
      await recorderRef.current.start();
      setRecording(true);
    } catch (error) {
      noteVoiceError(`Could not start recording: ${error instanceof Error ? error.message : String(error)}`);
    }
  };
  const stopRecording = async () => {
    if (!recording) return;
    setRecording(false);
    setTranscribing(true);
    try {
      const bytes = await recorderRef.current.stop();
      const { text } = await transport.transcribe(bytes);
      if (text) setDraft((prev) => prev ? `${prev} ${text}` : text);
    } catch (error) {
      noteVoiceError(`Could not transcribe: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setTranscribing(false);
    }
  };
  const busy = streaming || pendingConfirm !== null;
  const messages = thread.length === 0 ? [{ id: "greeting", role: "system", content: `${familiarName} is listening.` }] : thread.map((entry) => toMessage(entry, familiarName, respond));
  return /* @__PURE__ */ jsx3(
    ChatSurface,
    {
      messages,
      busy,
      value: draft,
      onValueChange: setDraft,
      onSend: send,
      placeholder: `Message ${familiarName}\u2026`,
      composerAccessory: mic ? /* @__PURE__ */ jsx3(
        MicButton,
        {
          recording,
          transcribing,
          disabled: busy,
          onStart: startRecording,
          onStop: stopRecording
        }
      ) : void 0
    }
  );
}
function MicButton({
  recording,
  transcribing,
  disabled,
  onStart,
  onStop
}) {
  const label = transcribing ? "Transcribing\u2026" : recording ? "Recording \u2014 release to transcribe" : "Record voice message";
  return /* @__PURE__ */ jsx3(
    Button,
    {
      type: "button",
      variant: recording ? "danger" : "ghost",
      size: "sm",
      "aria-label": label,
      "aria-pressed": recording,
      disabled: disabled || transcribing,
      onMouseDown: (event) => {
        event.preventDefault();
        onStart();
      },
      onMouseUp: (event) => {
        event.preventDefault();
        onStop();
      },
      onMouseLeave: () => {
        if (recording) onStop();
      },
      children: /* @__PURE__ */ jsx3("span", { "aria-hidden": true, children: transcribing ? "\u2026" : recording ? "\u25CF" : "\u{1F399}" })
    }
  );
}
function toMessage(entry, familiarName, respond) {
  if (entry.kind === "tool") {
    return { id: entry.id, role: "system", content: /* @__PURE__ */ jsx3(ToolCard, { entry }) };
  }
  if (entry.kind === "confirm") {
    return { id: entry.id, role: "system", content: /* @__PURE__ */ jsx3(ConfirmCard, { entry, respond }) };
  }
  return {
    id: entry.id,
    role: entry.role,
    content: entry.pending ? /* @__PURE__ */ jsx3("em", { children: `${familiarName} is thinking\u2026` }) : /* @__PURE__ */ jsx3("span", { style: { whiteSpace: "pre-wrap" }, children: entry.text })
  };
}
var TOOL_META = {
  running: { label: "running", intent: "info" },
  ok: { label: "done", intent: "success" },
  error: { label: "error", intent: "error" }
};
var CONFIRM_META = {
  pending: { label: "awaiting confirmation", intent: "warning" },
  confirmed: { label: "confirmed", intent: "success" },
  denied: { label: "denied", intent: "neutral" }
};
var cardStyle = { display: "flex", flexDirection: "column", gap: 6, fontSize: 13 };
var headRow = { display: "flex", alignItems: "center", gap: 8 };
function ToolCard({ entry }) {
  const meta = TOOL_META[entry.status];
  return /* @__PURE__ */ jsxs2(Card2, { variant: "flush", style: cardStyle, "aria-label": `tool ${entry.name}`, children: [
    /* @__PURE__ */ jsxs2("div", { style: headRow, children: [
      /* @__PURE__ */ jsx3("span", { "aria-hidden": true, children: "\u2699" }),
      /* @__PURE__ */ jsx3("code", { style: { fontWeight: 600 }, children: entry.name }),
      /* @__PURE__ */ jsx3(Badge2, { variant: meta.intent, children: meta.label })
    ] }),
    /* @__PURE__ */ jsx3(ArgList, { args: entry.args }),
    entry.result ? /* @__PURE__ */ jsx3(
      "pre",
      {
        style: {
          margin: 0,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          fontSize: 12,
          opacity: entry.status === "error" ? 1 : 0.85
        },
        children: entry.result
      }
    ) : null
  ] });
}
function ConfirmCard({
  entry,
  respond
}) {
  const meta = CONFIRM_META[entry.status];
  return /* @__PURE__ */ jsxs2(Card2, { variant: "raised", style: cardStyle, "aria-label": `confirm ${entry.name}`, children: [
    /* @__PURE__ */ jsxs2("div", { style: headRow, children: [
      /* @__PURE__ */ jsx3("span", { "aria-hidden": true, children: "\u26A0" }),
      /* @__PURE__ */ jsx3("code", { style: { fontWeight: 600 }, children: entry.name }),
      entry.level ? /* @__PURE__ */ jsx3(Badge2, { variant: "warning", children: entry.level }) : null,
      /* @__PURE__ */ jsx3(Badge2, { variant: meta.intent, children: meta.label })
    ] }),
    /* @__PURE__ */ jsx3(ArgList, { args: entry.args }),
    entry.status === "pending" ? /* @__PURE__ */ jsxs2("div", { style: { display: "flex", gap: 8 }, children: [
      /* @__PURE__ */ jsx3(Button, { variant: "primary", size: "sm", onClick: () => respond(entry, "confirm"), children: "Confirm" }),
      /* @__PURE__ */ jsx3(Button, { variant: "danger", size: "sm", onClick: () => respond(entry, "deny"), children: "Deny" })
    ] }) : null
  ] });
}
function ArgList({ args }) {
  const entries = Object.entries(args);
  if (entries.length === 0) return null;
  return /* @__PURE__ */ jsx3("dl", { style: { margin: 0, display: "grid", gridTemplateColumns: "auto 1fr", gap: "2px 8px" }, children: entries.map(([key, value]) => /* @__PURE__ */ jsxs2("div", { style: { display: "contents" }, children: [
    /* @__PURE__ */ jsx3("dt", { style: { opacity: 0.6, fontFamily: "monospace" }, children: key }),
    /* @__PURE__ */ jsx3("dd", { style: { margin: 0, wordBreak: "break-word" }, children: formatArg(value) })
  ] }, key)) });
}
function formatArg(value) {
  return typeof value === "string" ? value : JSON.stringify(value);
}

// src/testing.ts
var FakeTransport = class {
  constructor(canned) {
    this.canned = canned;
  }
  canned;
  async get(path) {
    const hit = this.canned[path];
    if (!hit) return { status: 404, body_json: null, familiar: null };
    return hit;
  }
  async post(path) {
    return this.get(path);
  }
  chat(_m, _s, _h) {
    throw new Error("FakeTransport.chat: override in a subclass to drive handlers");
  }
  async confirm(_token, _decision) {
    throw new Error("FakeTransport.confirm: override in a subclass");
  }
  async transcribe(_bytes) {
    throw new Error("FakeTransport.transcribe: override in a subclass");
  }
};
export {
  BriefPanel,
  ChatPanel,
  DaemonApiError,
  FakeTransport,
  FamiliarProvider,
  Recorder,
  createWebTransport,
  parseChatSSE,
  useBrief,
  useDaemon
};
